"use client";

import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { ChatHeader } from "@/components/chat-header";
import { OnboardingPlatformTour } from "@/components/onboarding-platform-tour";
import { PLATFORM_TOUR_STEP_TRADES } from "@/lib/platform-tour-constants";
import { CaseStudyCard } from "@/components/case-studies/CaseStudyCard";
import { BlogBuddyTour } from "@/components/buddy/BlogBuddyTour";
import { CASE_STUDIES } from "@/utils/caseStudiesCatalog";
import { Mail, X, ChevronDown, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, type TradeStats } from "@/lib/api-client";
import { isBlogTourVisit } from "@/lib/onboarding-constants";

const FILTER_PILLS = [
  { label: "All", value: "all" },
  { label: "Guides", value: "guide" },
  { label: "Options", value: "options" },
  { label: "Stocks", value: "stocks" },
  { label: "Earnings", value: "earnings" },
  { label: "Flow", value: "flow" },
  { label: "Swing", value: "swing" },
] as const;

const SORT_OPTIONS = [
  { label: "Newest", value: "newest" },
  { label: "Most Viewed", value: "views" },
  { label: "Most Liked", value: "likes" },
  { label: "Highest Return", value: "return" },
] as const;

type FilterValue = (typeof FILTER_PILLS)[number]["value"];
type SortValue = (typeof SORT_OPTIONS)[number]["value"];
const TOUR_TARGET_SLUG = "whale-watching-flow-strategy";

function matchesFilter(tags: string[], returnPct: string, filter: FilterValue): boolean {
  if (filter === "all") return true;
  if (filter === "guide") return returnPct === "Guide";
  const lower = tags.map((t) => t.toLowerCase());
  const map: Record<string, string[]> = {
    options: ["options", "options flow", "calls", "0dte", "short straddle", "vertical spreads", "flow analysis"],
    stocks: ["stocks", "short", "gex", "dealer positioning", "beginner"],
    earnings: ["earnings", "iv crush"],
    flow: ["options flow", "flow analysis", "automated"],
    swing: ["swing trade", "swing"],
  };
  return (map[filter] ?? []).some((kw) => lower.some((t) => t.includes(kw)));
}

function parseReturn(pct: string): number {
  const n = parseFloat(pct.replace(/[^0-9.\-]/g, ""));
  return isNaN(n) ? -Infinity : n;
}

function prioritizeWhaleWatching<T extends { slug: string; returnPct: string }>(
  items: T[],
  filter: FilterValue,
): T[] {
  const whale = items.find((item) => item.slug === TOUR_TARGET_SLUG);
  if (!whale) return items;
  const withoutWhale = items.filter((item) => item.slug !== TOUR_TARGET_SLUG);
  if (filter === "options") return [whale, ...withoutWhale];
  if (filter !== "all") return items;
  const guides = withoutWhale.filter((item) => item.returnPct === "Guide");
  const rest = withoutWhale.filter((item) => item.returnPct !== "Guide");
  return [...guides, whale, ...rest];
}

export default function BlogPageClient() {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const tourActive = isBlogTourVisit(searchParams);
  const [filter, setFilter] = useState<FilterValue>("all");
  const [sort, setSort] = useState<SortValue>("newest");
  const [sortOpen, setSortOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [statsMap, setStatsMap] = useState<Record<string, TradeStats>>({});

  // Fetch real engagement stats for all case studies
  useEffect(() => {
    const slugs = CASE_STUDIES.map((s) => s.slug);
    apiClient.getTradeBulkStats(slugs, user?.uid).then((res) => {
      const map: Record<string, TradeStats> = {};
      for (const s of res.stats) map[s.slug] = s;
      setStatsMap(map);
    }).catch(() => {});
  }, [user?.uid]);

  const filtered = useMemo(() => {
    const items = CASE_STUDIES.filter((s) => matchesFilter(s.tags, s.returnPct, filter));
    const sorted = [...items];
    switch (sort) {
      case "views":
        sorted.sort((a, b) => (statsMap[b.slug]?.view_count ?? b.views) - (statsMap[a.slug]?.view_count ?? a.views));
        break;
      case "likes":
        sorted.sort((a, b) => (statsMap[b.slug]?.like_count ?? b.likes) - (statsMap[a.slug]?.like_count ?? a.likes));
        break;
      case "return":
        sorted.sort((a, b) => parseReturn(b.returnPct) - parseReturn(a.returnPct));
        break;
      default:
        break;
    }
    return prioritizeWhaleWatching(sorted, filter);
  }, [filter, sort, statsMap]);

  // The blog onboarding tour specifically guides the user to the
  // "How I Use AI to Track Whale Trades and Make Consistent Money"
  // case study (slug: whale-watching-flow-strategy). We surface its
  // index in the current filtered/sorted grid so the right card gets
  // the tour ring + click_target. If it's been filtered out (e.g.
  // the user hasn't yet clicked the Options pill the tour points at),
  // the index is -1 and no card is targeted yet.
  const tourTargetIdx = useMemo(
    () => filtered.findIndex((s) => s.slug === TOUR_TARGET_SLUG),
    [filtered],
  );

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Trade-ideas leg of the cross-page onboarding chain. Activates
          when the URL carries ?onboarding_blog=1 (set by the home tour
          when the user clicks Browse more on the Top Trade Ideas pill). */}
      <BlogBuddyTour />
      {/* Cross-page platform tour — fires when URL carries
          ?ptour=trades. Runs the 2-beat /blog scene, then advances
          to /pricing?ptour=pricing (the conversion page). */}
      <OnboardingPlatformTour expectedStep={PLATFORM_TOUR_STEP_TRADES} />
      <ChatHeader
        chatId=""
        selectedVisibilityType="public"
        isReadonly={false}
        section="chat"
        title="Trade Ideas"
      />

      <div className="w-full max-w-[1140px] mx-auto px-4 sm:px-6 pt-4 pb-24">
        {/* ── HERO HEADER ── */}
        <div
          data-tour="trades-hero"
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
              Trade Ideas
            </h1>
            <p className="mt-1.5 sm:mt-2 text-sm sm:text-[15px] text-zinc-400 max-w-lg leading-relaxed">
              Real AI trading strategies from real traders. New ideas added every week.
            </p>
          </div>

          <button
            onClick={() => setSubmitOpen(true)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg shrink-0 self-start sm:self-auto",
              "bg-white/[0.06] border border-white/[0.10] text-zinc-300",
              "hover:bg-white/[0.10] hover:border-white/[0.15] hover:text-white transition-colors text-xs sm:text-sm font-medium"
            )}
          >
            <Send className="w-4 h-4" />
            Submit Your Trade
          </button>
        </div>

        {/* ── FILTERS + SORT ── */}
        <div
          data-tour="blog-filter-bar"
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTER_PILLS.map((pill) => (
              <button
                key={pill.value}
                onClick={() => setFilter(pill.value)}
                data-tour={
                  pill.value === "options" ? "blog-filter-options" : undefined
                }
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                  filter === pill.value
                    ? "bg-white/10 text-white border border-white/15"
                    : "text-zinc-500 hover:text-zinc-300 border border-transparent hover:border-white/[0.06]"
                )}
              >
                {pill.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 border border-white/[0.06] hover:border-white/10 transition-colors"
            >
              {SORT_OPTIONS.find((o) => o.value === sort)?.label}
              <ChevronDown className={cn("w-3 h-3 transition-transform", sortOpen && "rotate-180")} />
            </button>
            {sortOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setSortOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-40 min-w-[140px] rounded-lg border border-white/[0.08] bg-[#141416] shadow-[0_8px_32px_rgba(0,0,0,0.6)] py-1">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setSort(opt.value); setSortOpen(false); }}
                      className={cn(
                        "w-full text-left px-3 py-1.5 text-xs transition-colors",
                        sort === opt.value
                          ? "text-white bg-white/[0.06]"
                          : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── CARD GRID ── */}
        <div
          data-tour="trades-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {filtered.map((study, idx) => (
            <CaseStudyCard
              key={study.slug}
              study={study}
              className="w-full"
              likeCount={statsMap[study.slug]?.like_count}
              viewCount={statsMap[study.slug]?.view_count}
              tourActive={tourActive}
              tourTarget={tourActive && idx === tourTargetIdx}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-sm">No trade ideas match this filter.</p>
          </div>
        )}
      </div>

      {/* ── SUBMIT MODAL ── */}
      {submitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSubmitOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-xl border border-white/[0.08] bg-[#111113] shadow-[0_24px_80px_rgba(0,0,0,0.7)] p-6">
            <button
              onClick={() => setSubmitOpen(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/[0.10] flex items-center justify-center">
                <Send className="w-4 h-4 text-zinc-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Submit Your Trade</h2>
                <p className="text-xs text-zinc-500">Share your winning strategy with the community</p>
              </div>
            </div>

            <p className="text-sm text-zinc-400 leading-relaxed mb-5">
              Got a trade idea or strategy that worked? We feature the best submissions
              from our community. Send us the details (ticker, setup, results, and
              screenshots) and we{"'"}ll turn it into a full write-up.
            </p>

            <a
              href="mailto:hashims@xynth.finance?subject=Trade%20Idea%20Submission"
              className={cn(
                "flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg",
                "bg-white hover:bg-zinc-200 text-black font-semibold text-sm transition-colors"
              )}
            >
              <Mail className="w-4 h-4" />
              Send via Email
            </a>

            <p className="text-[11px] text-zinc-600 text-center mt-3">
              hashims@xynth.finance · We reply to every submission
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
